import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ArrowLeft, ArrowRight, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BatchDateBarProps {
  selectedCount: number;
  defaultMonth?: Date;
  isSaving?: boolean;
  onSetDate: (date: Date) => void;
  onShift: (days: number) => void;
  onClear: () => void;
  onDone: () => void;
}

/**
 * Sticky action bar shown while the itinerary is in "Select items" mode.
 * Lets the user move every ticked item to one date, or shift them all by
 * a number of days (preserving the gaps between them).
 */
export function BatchDateBar({
  selectedCount,
  defaultMonth,
  isSaving = false,
  onSetDate,
  onShift,
  onClear,
  onDone,
}: BatchDateBarProps) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [shiftDays, setShiftDays] = useState("1");
  const [confirm, setConfirm] = useState<
    | { kind: "set"; date: Date }
    | { kind: "shift"; days: number }
    | null
  >(null);

  const disabled = selectedCount === 0 || isSaving;
  const shiftAmount = Math.abs(parseInt(shiftDays, 10) || 0);

  const confirmText = () => {
    if (!confirm) return "";
    if (confirm.kind === "set") {
      return `${selectedCount} item${selectedCount === 1 ? "" : "s"} will move to ${format(
        confirm.date,
        "EEEE d MMMM yyyy"
      )}.`;
    }
    const n = Math.abs(confirm.days);
    return `${selectedCount} item${selectedCount === 1 ? "" : "s"} will move ${n} day${
      n === 1 ? "" : "s"
    } ${confirm.days < 0 ? "earlier" : "later"}.`;
  };

  const runConfirmed = () => {
    if (!confirm) return;
    if (confirm.kind === "set") onSetDate(confirm.date);
    else onShift(confirm.days);
    setConfirm(null);
  };

  return (
    <>
      <div className="sticky top-0 z-20 mb-3 rounded-lg border border-primary/40 bg-primary/5 p-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {selectedCount} item{selectedCount === 1 ? "" : "s"} selected
          </span>

          <div className="mx-1 hidden h-5 w-px bg-border sm:block" />

          {/* Set one date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn("justify-start font-normal", !date && "text-muted-foreground")}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "d MMM yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                defaultMonth={date ?? defaultMonth}
                autoFocus={false}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            disabled={disabled || !date}
            onClick={() => date && setConfirm({ kind: "set", date })}
          >
            Set date
          </Button>

          <div className="mx-1 hidden h-5 w-px bg-border sm:block" />

          {/* Shift by days */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Shift by</span>
            <Input
              type="number"
              min={1}
              value={shiftDays}
              onChange={(e) => setShiftDays(e.target.value)}
              className="h-9 w-16"
            />
            <span className="text-xs text-muted-foreground">day(s)</span>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || shiftAmount === 0}
              onClick={() => setConfirm({ kind: "shift", days: -shiftAmount })}
              title="Move earlier"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || shiftAmount === 0}
              onClick={() => setConfirm({ kind: "shift", days: shiftAmount })}
              title="Move later"
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClear} disabled={selectedCount === 0}>
              Clear
            </Button>
            <Button variant="secondary" size="sm" onClick={onDone}>
              <X className="mr-1 h-4 w-4" />
              Done
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Tick the items you want to move. Hotel stays keep the same number of nights.
        </p>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move selected items?</AlertDialogTitle>
            <AlertDialogDescription>{confirmText()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed}>Move items</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
